import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveModalBottomSheet } from '@/components';
import Button from '@/components/Button';
import type { CustomerPackage, SelectedPackage } from '@/models';
import { TimesBadge } from '@/pages/components/Badge';

interface TimesPackageBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  customerPackage: CustomerPackage | null;
  onConfirm: (selected: SelectedPackage) => Promise<void> | void;
  initialValue?: number;
}

const TimesPackageBottomSheet: React.FC<TimesPackageBottomSheetProps> = ({
  isOpen,
  onClose,
  customerPackage,
  onConfirm,
  initialValue,
}) => {
  const [count, setCount] = useState(
    initialValue !== undefined && initialValue > 0 ? initialValue : 1,
  );
  const { t } = useTranslation();
  // Sync when opened or when initialValue changes for same package
  useEffect(() => {
    if (isOpen) {
      setCount(
        initialValue !== undefined && initialValue > 0 ? initialValue : 1,
      );
    }
  }, [isOpen, initialValue, customerPackage?.id]);
  if (!customerPackage) return null;
  const remaining = customerPackage.remainingValue;
  const maxLabel = t('package.times.maximum', { remaining });

  const adjust = (delta: number) => {
    setCount((c) => {
      let next = c + delta;
      if (next < 1) next = 1;
      if (next > remaining) next = remaining;
      return next;
    });
  };

  const handleConfirm = async () => {
    await onConfirm({ id: customerPackage.id, value: count });
    onClose();
  };

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyFooter={
        <div className="px-4 pt-2 pb-4">
          <Button disabled={count <= 0} onClick={handleConfirm}>
            {t('common.confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 px-4 pt-2 pb-2">
        {/* Title & Badge */}
        <div className="flex flex-col gap-1">
          <h2 className="text-primary-text leading-6 font-medium">
            {customerPackage.name}
          </h2>
          <TimesBadge />
        </div>
        {/* Remains & Expiry */}
        <div className="border-divider grid grid-cols-2 gap-4 border-t border-b py-4 text-sm">
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.times.remains')}
            </span>
            <span className="text-primary-text leading-6 font-medium">
              {remaining}/{customerPackage.totalValue}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.times.expiry')}
            </span>
            <span className="text-primary-text leading-6">
              {new Date(customerPackage.expiryDate).toLocaleDateString('en-GB')}
            </span>
          </div>
        </div>
        {/* Count selector */}
        <div className="space-y-3">
          <div className="text-secondary-text text-center leading-6 font-semibold">
            {t('package.times.enterNumber')}
          </div>
          <div className="mx-auto flex h-10 w-[170px] items-center justify-center gap-2">
            <button
              type="button"
              className="border-primary text-primary flex size-9 items-center justify-center rounded border font-bold disabled:opacity-40"
              onClick={() => adjust(-1)}
              disabled={count <= 1}
              aria-label={t('package.times.decrease')}
            >
              -
            </button>
            <div className="text-gray-text flex-1 text-center leading-5 font-semibold">
              {count}
            </div>
            <button
              type="button"
              className="border-primary text-primary flex size-9 items-center justify-center rounded border font-bold disabled:opacity-40"
              onClick={() => adjust(1)}
              disabled={count >= remaining}
              aria-label={t('package.times.increase')}
            >
              +
            </button>
          </div>
          <div className="text-secondary-text text-center text-sm leading-5">
            {maxLabel}
          </div>
        </div>
      </div>
    </ResponsiveModalBottomSheet>
  );
};

export default TimesPackageBottomSheet;
